import { RDKitModule } from "@rdkit/rdkit";
import { useEffect, useRef, useState } from "react";
import useLazyLoad from "../../hooks/useLazyLoad";
import { CircularProgress } from "@mui/material";
import NoImage from '../../assets/Images/NoImage.png';

export default function MoleculeStructure({
    smiles,
    width,
    height,
    atom_bond_importance,
    highlightColorMode,
    id,
}: {
    smiles: string,
    width: number,
    height: number,
    atom_bond_importance?: any,
    highlightColorMode?: 'signed' | 'yellow',
    id?: string,
}) {
    const [ svg, setSVG ] = useState<string | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const isVisible = useLazyLoad(containerRef, {threshold: 0.1});

    const sanitizeImportanceMap = (importanceMap: any) => {
        if (!importanceMap || typeof importanceMap !== 'object') {
            return undefined;
        }

        const atomsRaw = importanceMap.atoms;
        const bondsRaw = importanceMap.bonds;
        if (!atomsRaw || !bondsRaw || typeof atomsRaw !== 'object' || typeof bondsRaw !== 'object') {
            return undefined;
        }

        const atoms = Object.fromEntries(
            Object.entries(atomsRaw)
                .map(([atomKey, atomValue]) => [Number(atomKey), Number(atomValue)])
                .filter(([atomKey, atomValue]) => Number.isFinite(atomKey) && atomKey >= 0 && Number.isInteger(atomKey) && Number.isFinite(atomValue))
        ) as { [key: number]: number };

        const bonds = Object.fromEntries(
            Object.entries(bondsRaw)
                .map(([bondKey, bondValue]) => [Number(bondKey), Number(bondValue)])
                .filter(([bondKey, bondValue]) => Number.isFinite(bondKey) && bondKey >= 0 && Number.isInteger(bondKey) && Number.isFinite(bondValue))
        ) as { [key: number]: number };

        return {
            atoms,
            bonds,
        };
    };

    useEffect(() => {
        if (!window.RDKit) return;

        const instance = window.RDKit;
        if (smiles && instance) {
            let highlighted_atoms: number[] | undefined;
            let highlighted_bonds: number[] | undefined;
            let atom_colors = {};
            let bond_colors = {};

            if (atom_bond_importance) {
                const atomBonds = sanitizeImportanceMap(atom_bond_importance);
                if (!atomBonds) {
                    drawWithOptionalHighlighting(instance, smiles);
                    return;
                }

                const { atoms, bonds } = atomBonds;

                highlighted_atoms = Object.keys(atoms).map(key => Number(key));
                highlighted_bonds = Object.keys(bonds).map(key => Number(key));

                if (highlighted_atoms.length === 0 && highlighted_bonds.length === 0) {
                    drawWithOptionalHighlighting(instance, smiles);
                    return;
                }

                if (highlightColorMode === 'yellow') {
                    highlighted_atoms.forEach(highlighted_atom => {
                        (atom_colors as any)[highlighted_atom] = [1, 1, 0];
                    });
                    highlighted_bonds.forEach(highlighted_bond => {
                        (bond_colors as any)[highlighted_bond] = [1, 1, 0];
                    });
                } else {
                    let atom_colors_red = {};
                    let atom_colors_green = {};
                    let atom_colors_blue = {};

                    highlighted_atoms.forEach(highlighted_atom => {
                        (atom_colors_red as any)[highlighted_atom] = -1 * Math.min(0, atoms[highlighted_atom]);
                        (atom_colors_green as any)[highlighted_atom] = 0;
                        (atom_colors_blue as any)[highlighted_atom] = Math.max(0, atoms[highlighted_atom])
                    });

                    let bond_colors_red = {};
                    let bond_colors_green = {};
                    let bond_colors_blue = {};

                    highlighted_bonds.forEach(highlighted_bond => {
                        (bond_colors_red as any)[highlighted_bond] = -1 * Math.min(0, bonds[highlighted_bond]);
                        (bond_colors_green as any)[highlighted_bond] = 0;
                        (bond_colors_blue as any)[highlighted_bond] = Math.max(0, bonds[highlighted_bond])
                    });

                    Object.keys(atom_colors_blue).map(atom => Number(atom)).forEach(atom => {
                        (atom_colors as any)[atom] = [
                            Math.abs(1 - (atom_colors_blue as any)[atom] - (atom_colors_green as any)[atom]),
                            Math.abs(1 - (atom_colors_red as any)[atom] - (atom_colors_blue as any)[atom]),
                            Math.abs(1 - (atom_colors_red as any)[atom] - (atom_colors_green as any)[atom]),
                        ]
                    });

                    Object.keys(bond_colors_blue).map(bond => Number(bond)).forEach(bond => {
                        (bond_colors as any)[bond] = [
                            Math.abs(1 - (bond_colors_blue as any)[bond] - (bond_colors_green as any)[bond]),
                            Math.abs(1 - (bond_colors_red as any)[bond] - (bond_colors_blue as any)[bond]),
                            Math.abs(1 - (bond_colors_red as any)[bond] - (bond_colors_green as any)[bond]), 
                        ]
                    });
                }

            };

            drawWithOptionalHighlighting(
                instance,
                smiles,
                highlighted_atoms,
                highlighted_bonds,
                atom_colors,
                bond_colors,
            )
        }

        //eslint-disable-next-line
    }, [atom_bond_importance, smiles, highlightColorMode]);

    function drawWithOptionalHighlighting(
        RDKit: RDKitModule, 
        smilesString: string,
        highlighted_atoms?: number[],
        highlighted_bonds?: number[],
        atom_colors?: { [key: number]: number[] },
        bond_colors?: { [key: number]: number[] },
    ) {
        let newSvg = null;

        if (RDKit) {
            const mol = RDKit.get_mol(smilesString);
            if (mol) {

                if (highlighted_atoms && highlighted_bonds && atom_colors && bond_colors) {
                    try {
                        const svgDetailsWithHighlight = {
                            width,
                            height,
                            bondLineWidth: 1,
                            atoms: highlighted_atoms,
                            bonds: highlighted_bonds,
                            highlightAtomColors: atom_colors,
                            highlightBondColors: bond_colors,
                            highlightBondWidthMultiplier: 16,
                            addAtomIndices: true,
                        };

                        newSvg = mol.get_svg_with_highlights(JSON.stringify(svgDetailsWithHighlight));
                    } catch {
                        const svgDetails = {
                            width,
                            height,
                            bondLineWidth: 1,
                        };
                        newSvg = mol.get_svg_with_highlights(JSON.stringify(svgDetails));
                    }

                } else {
                    const svgDetails = {
                        width,
                        height,
                        bondLineWidth: 1,
                    };

                    newSvg = mol.get_svg_with_highlights(JSON.stringify(svgDetails));
                }
            }
        };

        setSVG(newSvg);
    }

    return (
        <div ref={containerRef}>
            {!isVisible ? (
                <div className="text-disable p-4"><CircularProgress size={width-50} color='inherit'/></div>
            ) : svg ? (
                <div
                    id={id ? id : 'molecule-structure'}
                    title={smiles}
                    className="molecule-structure-svg"
                    dangerouslySetInnerHTML={{__html: svg}}
                ></div>
            ) : (
                <div style={{ width }} className="aspect-square p-4 rounded flex flex-col gap-3 justify-center items-center">
                    <img src={NoImage} className="w-full" alt="no-src-icon"/>
                    <p className="text-disable font-medium">No Image</p>
                </div>
            )}
        </div>
    )
    
}